<template>
  <div>
    <div v-if="!!user.is_admin" class="flex justify-content-center m-2">
      <Button data-cy="show-prod-rates-btn" @click="showFluidProperties = true">
        Show Diagnostics</Button
      >
    </div>
    <Dialog
      v-model:visible="showFluidProperties"
      header="Fluid Properties"
      :draggable="false"
      modal
      data-cy="prod-rates-dialog"
      class="flex w-screen h-screen"
    >
      <div class="flex flex-column gap-4">
        <div class="flex gap-1">
          <Button @click="onCopySizing">Copy Sizing</Button>
          <Button @click="onCopyScenario">Copy Scenario</Button>
        </div>
        <div class="flex gap-1">
          <Button
            :class="`${activeView === 'fluid-properties' ? activeButtonColor : ''}`"
            @click="activeView = 'fluid-properties'"
            >Fluid Properties</Button
          >
          <Button
            :class="`${activeView === 'calc-flow' ? activeButtonColor : ''}`"
            @click="activeView = 'calc-flow'"
            >Calc Flow</Button
          >
          <Button
            :class="`${activeView === 'calc-hz' ? activeButtonColor : ''}`"
            @click="activeView = 'calc-hz'"
            >Calc Hz</Button
          >
        </div>
      </div>
      <div v-if="activeView === 'fluid-properties'" class="flex justify-center">
        <div class="flex flex-column">
          <div class="flex">
            <InputText
              v-model:model-value="fluidPropertiesFilter"
              class="w-full"
            ></InputText>
          </div>
          <div class="flex justify-content-between p-2">
            <Button @click="onSelectAll">Select All</Button>
            <Button @click="onDeselectAll">Deselect All</Button>
            <Button @click="onCopy">Copy</Button>
          </div>
          <div class="flex flex-column gap-4 flex-wrap">
            <div
              v-for="section of sections"
              :key="section.key"
              class="flex items-center gap-1"
            >
              <Checkbox
                v-model="sectionSelections"
                :inputId="section.key"
                name="section"
                :value="section.name"
              />
              <label :for="section.key">{{ section.name }}</label>
            </div>
          </div>
        </div>
        <div class="p-4">
          <vue-json-pretty
            :data="filteredFluidPropertiesData"
            :path-collapsible="isGasPropertiesPath"
            :show-icon="true"
            :show-length="true"
          ></vue-json-pretty>
        </div>
      </div>
      <div v-if="activeView === 'calc-flow'" class="flex justify-center grow-1">
        <CalcFlowTester />
      </div>
      <div v-if="activeView === 'calc-hz'" class="flex justify-center grow-1">
        <CalcHzTester />
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useDiagnosticsStore } from "@/store";
import { computed, ref, watch } from "vue";
import {
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import useUserInfo from "@/composables/auth/useUserInfo";
import { storeToRefs } from "pinia";
import CalcFlowTester from "@/views/scenarios/predictiveCalcs/CalcFlowTester.vue";
import CalcHzTester from "@/views/scenarios/predictiveCalcs/CalcHzTester.vue";
import type { NodeDataType } from "vue-json-pretty/types/components/TreeNode";

type ActiveView = "fluid-properties" | "calc-flow" | "calc-hz";
const activeView = ref<ActiveView>("fluid-properties");
const activeButtonColor = "bg-gray-300";

const user = useUserInfo();

const scenarioCalculations = injectStrict(scenarioCalculationsKey);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey);

const equipmentDepths = computed(() => {
  const datumMDFt = scenarioCalculations.equipmentLengths.datumPointFt.value;
  const perfsMDFt =
    scenarioCalculations.basicData.value.topOfPerforationsMDFt || 0;
  const perfsTVDFt =
    scenarioCalculations.basicData.value.topOfPerforationsTVDFt || 0;
  const sensorMDFt =
    scenarioCalculations.equipmentLengths.bottomOfEquipmentDepthFt.value;
  const motorMDFt =
    scenarioCalculations.equipmentLengths.bottomOfMotorDepthFt.value;
  const sealsMDFt =
    scenarioCalculations.equipmentLengths.bottomOfSealsDepthFt.value;
  const intakeMDFt =
    scenarioCalculations.equipmentLengths.bottomOfIntakesDepthFt.value;
  const pumpMDFt =
    scenarioCalculations.equipmentLengths.bottomOfPumpsDepthFt.value;

  return {
    datum: {
      md: datumMDFt,
      tvd: scenarioCalculations.equipmentLengths.datumPointTVD.value
    },
    perfs: {
      md: perfsMDFt,
      tvd: perfsTVDFt
    },
    sensor: {
      md: sensorMDFt,
      tvd: scenarioCalculations.equipmentLengths.bottomOfEquipmentTVDFt.value
    },
    motor: {
      md: motorMDFt,
      tvd: scenarioCalculations.equipmentLengths.bottomOfMotorTVDFt.value
    },
    seals: {
      md: sealsMDFt,
      tvd: scenarioCalculations.equipmentLengths.bottomOfSealsTVDFt.value
    },
    intake: {
      md: intakeMDFt,
      tvd: scenarioCalculations.equipmentLengths.bottomOfIntakesTVDFt.value
    },
    pump: {
      md: pumpMDFt,
      tvd: scenarioCalculations.equipmentLengths.bottomOfPumpsTVDFt.value
    }
  };
});

const allData = computed<{ [key: string]: unknown }>(() => {
  return {
    wellMaxFlowRate: scenarioCalculations.maxFlowRateBBLD.value,
    wellMaxFlowRateAtIntake: scenarioCalculations.maxFlowRateAtIntakeBBLD.value,
    wellTestFlowRates: scenarioCalculations.wellTestFlowRates.value,
    pressureAtDatum: scenarioCalculations.wellFlowingPressureAtDatumPSIA.value,
    fluidPropertiesAtDatum:
      scenarioCalculations.fluidProperties.fluidPropertiesAtDatum.value,
    fluidPropertiesAtReservoir:
      scenarioCalculations.fluidProperties.fluidPropertiesAtReservoir.value,
    desiredFlowRates: scenarioCalculations.surfaceFlowRates.value,
    pressureForWellPerfs:
      scenarioCalculations.wellFlowingPressureAtPerfsPSIA.value,
    temperatureForWellPerfs:
      scenarioCalculations.downholeWellPerfsTemperatureF.value,
    fluidPropertiesAtWellPerfs:
      scenarioCalculations.fluidProperties.fluidPropertiesAtWellPerfs.value,
    pressureForSensor:
      scenarioCalculations.wellFlowingPressureAtSensorPSIA.value,
    temperatureForSensor: scenarioCalculations.downholeSensorTemperatureF.value,
    fluidPropertiesAtSensor:
      scenarioCalculations.fluidProperties.fluidPropertiesAtSensor.value,
    pressureForMotor:
      scenarioCalculations.motorCalculations.wellFlowingPressureAtMotorPSIA
        .value,
    temperatureForMotor: scenarioCalculations.downholeMotorTemperatureF.value,
    fluidPropertiesAtMotor:
      scenarioCalculations.fluidProperties.fluidPropertiesAtMotor.value,
    pressureForSeals:
      scenarioCalculations.sealCalculations.aggregate
        .wellFlowingPressureAtSealPSIA.value,
    temperatureForSeals:
      scenarioCalculations.sealCalculations.aggregate.downholeSealTemperatureF
        .value,
    fluidPropertiesAtSeals:
      scenarioCalculations.fluidProperties.fluidPropertiesAtSeals.value,

    pressureForIntake: scenarioCalculations.intakePressurePSIA.value,
    temperatureForIntake: scenarioCalculations.pumpIntakeTemperatureF.value,
    fluidPropertiesAtIntake:
      scenarioCalculations.fluidProperties.fluidPropertiesAtIntake.value,
    pressureForPumpIntake: scenarioCalculations.pumpIntakePressurePSIA.value,
    temperatureForPumpIntake: scenarioCalculations.pumpIntakeTemperatureF.value,
    fluidPropertiesAtPumpIntake:
      scenarioCalculations.fluidProperties.fluidPropertiesAtPumpIntake.value,
    temperatureForAfterAllGasSepAtPump:
      scenarioCalculations.pumpIntakeTemperatureF.value,
    fluidPropertiesAfterAllGasSeparationAtPump:
      scenarioCalculations.fluidProperties
        .fluidPropertiesAfterAllGasSeparationAtPump.value,
    temperatureForInsideTubing:
      scenarioCalculations.downholeTubingTemperatureF.value,
    fluidPropertiesContextForTubingFriction:
      scenarioCalculations.fluidPropertiesInsideTubingContext.value,
    topTubingProperties: scenarioCalculations.topTubingFrictionProperties.value,
    bottomTubingProperties:
      scenarioCalculations.bottomTubingFrictionProperties.value,
    tubingFrictionLossPSIA: scenarioCalculations.tubingFrictionLossPSIA.value,
    casingFrictionLossPSIA: scenarioCalculations.casingFrictionLossPSIA.value,
    perfsAndDatumCasingFrictionPSIA:
      scenarioCalculations.perfsAndDatumCasingFrictionPSIA.value,
    bottomOfEquipmentCasingFrictionPSIA:
      scenarioCalculations.bottomOfEquipmentCasingFrictionPSIA.value,
    sensorAndMotorCasingFrictionPSIA:
      scenarioCalculations.sensorAndMotorCasingFrictionPSIA.value,
    motorAndSealsCasingFrictionPSIA:
      scenarioCalculations.motorAndSealsCasingFrictionPSIA.value,
    sealsAndIntakesCasingFrictionPSIA:
      scenarioCalculations.sealsAndIntakesCasingFrictionPSIA.value,
    fluidPropertiesAfterPumpDischargeTubingContext:
      scenarioCalculations.fluidPropertiesAfterPumpDischargeTubingContext.value,
    equipmentDepths: equipmentDepths.value
  };
});

const fluidPropertiesFilter = ref("");
const showFluidProperties = ref(false);

const diagnosticsStore = useDiagnosticsStore();

const sections = ref(
  Object.keys(allData.value).map(s => {
    return {
      name: s,
      key: s
    };
  })
);
const { sectionSelections } = storeToRefs(diagnosticsStore);

const isGasPropertiesPath = (node: NodeDataType) => {
  return node.path.endsWith(".gasProperties");
};

const filteredFluidPropertiesData = computed(() => {
  return Object.keys(allData.value)
    .filter(s => sectionSelections.value.find(ss => ss === s))
    .reduce((a, s) => {
      return {
        ...a,
        [s]: allData.value[s]
      };
    }, {});
});

watch(sectionSelections.value, () => {
  diagnosticsStore.setSectionSelections(sectionSelections.value);
});

const onCopy = () => {
  navigator.clipboard.writeText(`
http://localhost:8009/sizings/${sizing.value?.id}/scenarios/${scenario.value?.id}/well

const diagnostics = ${JSON.stringify(filteredFluidPropertiesData.value, null, 2)}`);
  alert("COPIED!");
};

const onSelectAll = () => {
  sectionSelections.value = Object.keys(allData.value);
};

const onDeselectAll = () => {
  sectionSelections.value = [];
};

const onCopySizing = () => {
  const sizingJSON = `const sizing = ${JSON.stringify(sizing.value, null, 2)}`;
  navigator.clipboard.writeText(sizingJSON);
  alert("COPIED!");
};

const onCopyScenario = () => {
  const sizingJSON = `const scenario = ${JSON.stringify(scenario.value, null, 2)}`;
  navigator.clipboard.writeText(sizingJSON);
  alert("COPIED!");
};
</script>
